import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import {
  fetchAdminArchivedUsers,
  fetchAdminMetrics,
  fetchAdminReviews,
  fetchAdminUsers,
  type AdminArchivedUserRow,
  type AdminMetricsPayload,
  type AdminReviewRow,
  type AdminUserRow,
} from '../../lib/apiClient';
import { ADMIN_EXPORTS } from '../../lib/apiClient';
import { routes } from '../../lib/assessment/routes';
import { usePageMeta } from '../../lib/usePageMeta';
import { AverageScores } from './AverageScores';
import { HorizontalBarChart, VerticalBarChart } from './BarChart';
import { KeyMetrics } from './KeyMetrics';
import { ArchivedUsersTable } from './ArchivedUsersTable';
import { ExportControl } from './ExportEmails';
import { ReviewsSection } from './ReviewsSection';
import { UsersTable } from './UsersTable';

/** Production's `refetchInterval` on the console's reads. */
export const ADMIN_POLL_MS = 60_000;

type State =
  | { phase: 'loading' }
  | { phase: 'error' }
  | {
      phase: 'ok';
      metrics: AdminMetricsPayload;
      users: AdminUserRow[];
      archived: AdminArchivedUserRow[];
      reviews: AdminReviewRow[];
    };

/**
 * `/admin` — the administrative console.
 *
 * **This is a migration surface, not a design.** Production serves the whole
 * console as one page at one route, and everything about the shape below is
 * copied from production's own `pages/admin.tsx@11ca09a` rather than decided
 * here. Where production carries a defect, this page reproduces the defect.
 *
 * **The source supersedes the specification wherever they differ.** The spec
 * was written by observing a running console, so it records what a populated
 * production could be seen doing and marks the rest UNKNOWN. Several of those
 * unknowns — the page heading, the empty states, which sections are
 * conditional — are plainly readable in the source, and are taken from it.
 *
 * **One page, one route.** Production has no admin shell — no sidebar, no
 * section navigation, no breadcrumb — because there is nothing to navigate
 * between. `/admin/users`, `/admin/customers` and every other sibling render
 * Not Found there, and they do here too: the routing table registers `/admin`
 * alone, so the application's own fallback answers the rest.
 *
 * **One load, one error.** Production fires its console reads in parallel and
 * shows a single `Error loading admin data` when the load fails, so the reads
 * live here rather than in each section, and **any** of them failing produces
 * that one error. The sections are pure renderers.
 *
 * **Section order is normative** (spec §1):
 *
 *   Action bar → Key Metrics → Average Scores → Bottleneck Types →
 *   Bottleneck Commonalities → Work Fit Profiles → Users → Archived Users →
 *   Reviews
 *
 * The remaining sections arrive in their own slices, each inserted at its place
 * in that order. Nothing is rendered as a placeholder while it waits: an empty
 * section header tells an operator the console is broken, which is further from
 * production than not drawing it at all.
 *
 * Delivered so far: the action bar, **Key Metrics**, **Average Scores**, the
 * three charts, **Users**, **Archived Users**, and **Reviews** — production's
 * nine sections, all of them.
 */
export function AdminConsole() {
  usePageMeta({ title: 'Admin | High Performer OS', noindex: true });

  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<State>({ phase: 'loading' });
  /**
   * Bumped by every operator action that lands. Production refetches the
   * console after a mutation, so an archived customer leaves the table and
   * the counts above it move in the same breath.
   */
  const [reloads, setReloads] = useState(0);
  const reload = () => setReloads((n) => n + 1);
  /** Whether the console has rendered its data once. See the effect below. */
  const loaded = useRef(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    /*
      Only the first load shows the loading line. Production refetches after a
      mutation in the background and keeps the console on screen throughout; a
      console that blanked itself after every action would throw the operator
      back to the top of the page, and would discard the sections' own state —
      which row is open, which customer was just granted reports.
    */
    if (!loaded.current) setState({ phase: 'loading' });

    void (async () => {
      // In parallel, as production loads them. One failure is the page's error:
      // a console that rendered half of itself would leave an operator unsure
      // which half.
      const [metrics, users, archived, reviews] = await Promise.all([
        fetchAdminMetrics(token),
        fetchAdminUsers(token),
        fetchAdminArchivedUsers(token),
        fetchAdminReviews(token),
      ]);
      if (cancelled) return;
      loaded.current = true;
      setState(
        metrics.state === 'ok' &&
          users.state === 'ok' &&
          archived.state === 'ok' &&
          reviews.state === 'ok'
          ? {
              phase: 'ok',
              metrics: metrics.metrics,
              users: users.users,
              archived: archived.users,
              reviews: reviews.reviews,
            }
          : { phase: 'error' },
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [token, reloads]);

  /**
   * Production polls the console every sixty seconds — `refetchInterval:
   * 60_000` on the stats, the users, and the archive. An operator watching a
   * launch sees the numbers move without touching anything, and that is a
   * behaviour, not a detail of how the data is fetched.
   *
   * It rides the same `reloads` counter as the mutations, so a tick is an
   * ordinary background refetch: nothing blanks, and a tick that fails shows
   * the one page error exactly as a failed reload does.
   */
  useEffect(() => {
    if (!token) return;
    const timer = setInterval(reload, ADMIN_POLL_MS);
    return () => clearInterval(timer);
  }, [token]);

  return (
    <div className="flex flex-col gap-10" data-testid="admin-console">
      <AdminActionBar />

      {/*
        Production's first load is a centred spinner, not a sentence — it shows
        `Loader2` spinning and no words at all. The accessible name is supplied
        rather than left to the icon, because a spinner with no name announces
        nothing to a screen reader.
      */}
      {state.phase === 'loading' ? (
        <p
          className="flex items-center justify-center py-16"
          data-testid="admin-metrics-loading"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <span className="sr-only">Loading admin data…</span>
        </p>
      ) : null}

      {state.phase === 'error' ? (
        <p
          className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 font-medium"
          data-testid="admin-metrics-error"
          role="status"
          aria-live="polite"
        >
          Error loading admin data
        </p>
      ) : null}

      {state.phase === 'ok' ? (
        <>
          <KeyMetrics metrics={state.metrics} />

          {/* Production gates the gauges on there being any assessment at all. */}
          {state.metrics.totalAssessments > 0 ? (
            <AverageScores
              count={state.metrics.bottleneckAssessments}
              scores={state.metrics.averageScores}
            />
          ) : null}

          <VerticalBarChart
            testId="chart-bottleneck-types"
            title="Bottleneck Types"
            caption="Distribution of Bottleneck performer types."
            data={state.metrics.typeBreakdown}
            shorten
          />

          <HorizontalBarChart
            testId="chart-bottleneck-commonalities"
            title="Bottleneck Commonalities"
            caption="Most frequently surfaced bottlenecks across Bottleneck assessments."
            data={state.metrics.bottleneckBreakdown}
          />

          {/*
            The third chart is conditional where the first two are not:
            production wraps Work Fit Profiles in a length check and lets the
            other two render their own empty state instead.
          */}
          {state.metrics.workFitTypeBreakdown.length > 0 ? (
            <VerticalBarChart
              testId="chart-work-fit-profiles"
              title="Work Fit Profiles"
              caption="Distribution of Work Fit profile types."
              data={state.metrics.workFitTypeBreakdown}
              shorten
            />
          ) : null}

          <UsersTable users={state.users} onChanged={reload} />

          {/* Production draws the archive only when it holds somebody. */}
          {state.archived.length > 0 ? (
            <ArchivedUsersTable users={state.archived} onChanged={reload} />
          ) : null}

          <ReviewsSection reviews={state.reviews} onChanged={reload} />
        </>
      ) : null}
    </div>
  );
}

/**
 * The action bar (spec §3).
 *
 * Production carries five controls: four CSV exports, then **Back to
 * Dashboard** at the right.
 *
 * All five are here, in production's order.
 */
function AdminActionBar() {
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6"
      data-testid="admin-action-bar"
    >
      {/*
        Production's heading and its subtitle, from the console's own source.
        Both were previously flagged as unobserved and stood in as `Admin`.
      */}
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-semibold tracking-tight">Admin Console</h1>
        <p className="text-muted-foreground" data-testid="admin-subtitle">
          Operational overview, analytics, and review moderation.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {ADMIN_EXPORTS.map((exportControl) => (
          <ExportControl
            key={exportControl.path}
            label={exportControl.label}
            path={exportControl.path}
          />
        ))}

        <Link
          href={routes.dashboard}
          data-testid="admin-back-to-dashboard"
          className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}
